import React, { useState, useEffect, useMemo, useCallback } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Text, Card, Tabs, LoadingOverlay, Group } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { useSearchParams } from 'react-router-dom'
import { getGlobalSettings, updateGlobalSetting, updateGlobalSettingBulkUpdate, getLogisticRates, createLogisticRate, updateWholesalePrice, deleteWholesalePrice, deleteLogisticRates, deleteGlobalSettingBulkDelete } from '../../../../services/global-settings'
import { getAllCountryList } from '../../../../services/location'
import { notifications } from '@mantine/notifications'
import { IconPlane, IconShip, IconTruck, IconPackage } from '@tabler/icons-react'
import ModalDetailRate from './ModalDetailRate'
import ModalEditRate from './ModalEditRate'
import { modals } from '@mantine/modals'

// New Components
import ShipmentMethodSection from './components/ShipmentMethodSection'
import GeneralConfiguration from './components/GeneralConfiguration'
import FreightRatePanel from './components/FreightRatePanel'
import AirCourierPanel from './components/AirCourierPanel'

const METHOD_GROUPS = {
    official: [
        { value: 'air', label: 'Air Cargo', icon: IconPlane },
        { value: 'ocean', label: 'Ocean Cargo', icon: IconShip },
        { value: 'trucking', label: 'Trucking', icon: IconTruck },
        { value: 'air_courier', label: 'Air Courier', icon: IconPackage },
    ],
    unofficial: [
        { value: 'air_dtd', label: 'Air Cargo Door To Door', icon: IconPlane },
        { value: 'ocean_dtd', label: 'Ocean Cargo Door To Door', icon: IconShip },
        { value: 'trucking_unofficial', label: 'Trucking Un-Official', icon: IconTruck },
    ]
}

const ALL_METHODS = [...METHOD_GROUPS.official, ...METHOD_GROUPS.unofficial]

const mappingRoute = [
    { label: '/', route: null },
    { label: 'Settings', route: null },
    { label: 'International Shipping Rate List', route: '/settings/international-shipping-rates' },
    { label: 'Set Rates', route: null },
]

const SetRates = () => {
    const dispatch = useDispatch()
    const [searchParams] = useSearchParams()

    const sourceId = searchParams.get('source_id')
    const destId = searchParams.get('dest_id')

    const [loading, setLoading] = useState(false)
    const [pageLoading, setPageLoading] = useState(false)

    // Form State
    const [availableMethods, setAvailableMethods] = useState([]) // Array of strings (values)
    const [originalMethods, setOriginalMethods] = useState([])
    const [methodsToDelete, setMethodsToDelete] = useState([])
    const [officialVat, setOfficialVat] = useState('')
    const [routes, setRoutes] = useState('')
    const [defaultMethod, setDefaultMethod] = useState(null)

    const [rates, setRates] = useState({})

    // Air Courier State (Different data source)
    const [airCourierRates, setAirCourierRates] = useState([])
    const [airCourierIncluded, setAirCourierIncluded] = useState({})
    const [airCourierLoading, setAirCourierLoading] = useState(false)
    const [activeTab, setActiveTab] = useState(null)

    // Edit Modal State
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [editModalLoading, setEditModalLoading] = useState(false)
    const [editingRate, setEditingRate] = useState(null)
    const [editFormData, setEditFormData] = useState({
        shipping_types: 'International',
        shipping_method: 'courier',
        origin_country_id: null,
        origin_type: 'cities',
        destination_country_id: null,
        destination_type: 'Country',
        origin_id: null,
        origin_name: '',
        destination_id: null,
        destination_name: '',
        calculation: 'flat',
        currency: 'IDR',
        weight_unit: 'kg',
        courier_id: null,
        courier_name: '',
        minimum: 1,
        maximum: 1000,
        insurance: false,
        insurance_percentage: 0,
        remarks: '',
        name: '',
        advance_rates: [] // [{rate_per_weight, rate}]
    })

    // Detail Modal State
    const [detailModalOpen, setDetailModalOpen] = useState(false)
    const [detailModalLoading, setDetailModalLoading] = useState(false)
    const [detailRateInfo, setDetailRateInfo] = useState(null)
    const [detailRateList, setDetailRateList] = useState([])

    // Context Info
    const [sourceCountry, setSourceCountry] = useState(null)
    const [destCountry, setDestCountry] = useState(null)



    useEffect(() => {
        dispatch(updateRoute({ 'data': mappingRoute }))
    }, [dispatch])

    // Load Country Names
    useEffect(() => {
        const fetchCountries = async () => {
            if (!sourceId && !destId) return
            try {
                const response = await getAllCountryList({
                    'filter[id][in]': `${sourceId},${destId}`,
                    'page[limit]': 10
                })

                if (response.success) {
                    const source = response.data.find(c => String(c.id) === String(sourceId))
                    const dest = response.data.find(c => String(c.id) === String(destId))
                    setSourceCountry(source)
                    setDestCountry(dest)
                }
            } catch (error) {
                console.error("Failed to fetch countries", error)
            }
        }
        fetchCountries()
    }, [sourceId, destId])

    // Load Settings
    useEffect(() => {
        const loadSettings = async () => {
            if (!sourceId || !destId) return
            setPageLoading(true)
            try {
                const params = {
                    'page[num]': 1,
                    'page[limit]': 100,
                    'filter[country_id][in]': destId,
                    'filter[source_country_id][in]': sourceId,
                    'filter[key][in]': 'available_shipment_methods,official_vat,official_routes,default_shipment_method',
                    'fields[-global_settings]': 'updated_by'
                }

                const response = await getGlobalSettings(params)
                if (response.success && response.data) {
                    const data = response.data

                    const methodSettings = data.filter(d => d.attributes.key === 'available_shipment_methods')
                    setOriginalMethods(methodSettings)
                    setAvailableMethods(methodSettings.map(d => d.attributes.value))

                    const vat = data.find(d => d.attributes.key === 'official_vat')
                    if (vat) setOfficialVat(vat.attributes.value)

                    const route = data.find(d => d.attributes.key === 'official_routes')
                    if (route) setRoutes(route.attributes.value)

                    const def = data.find(d => d.attributes.key === 'default_shipment_method')
                    if (def) setDefaultMethod(def.attributes.value)
                }
            } catch (error) {
                console.error(error)
                notifications.show({ title: 'Error', message: 'Failed to load settings', color: 'red' })
            } finally {
                setPageLoading(false)
            }
        }
        loadSettings()
    }, [sourceId, destId])

    const [rateSettingsData, setRateSettingsData] = useState([])

    const RATE_KEYS = useMemo(() => ({
        air: {
            currency: 'official_weight_currency',
            cost: 'official_weight_cost',
            min_value: 'official_air_product_min_value',
            weight_min: 'official_min_weight',
            step: 'official_weight_step',
            eta: 'official_air_eta',
            promo_role: 'promo_air_by_role'
        },
        ocean: {
            currency: 'official_cbm_currency',
            cost: 'official_cbm_cost',
            min_value: 'official_ocean_product_min_value',
            weight_min: 'official_min_cbm',
            step: 'official_cbm_step',
            eta: 'official_ocean_eta',
            promo_role: 'promo_ocean_by_role'
        },
        air_dtd: {
            currency: 'unofficial_weight_currency',
            cost: 'unofficial_weight_cost',
            min_value: 'unofficial_air_product_min_value',
            weight_min: 'unofficial_min_weight',
            step: 'unofficial_weight_step',
            eta: 'unofficial_air_eta',
            promo_role: 'promo_air_dtd_by_role'
        },
        ocean_dtd: {
            currency: 'unofficial_cbm_currency',
            cost: 'unofficial_cbm_cost',
            min_value: 'unofficial_ocean_product_min_value',
            weight_min: 'unofficial_min_cbm',
            step: 'unofficial_cbm_step',
            eta: 'unofficial_ocean_eta',
            promo_role: 'promo_ocean_dtd_by_role'
        },
        trucking: {
            currency: 'official_trucking_currency',
            cost: 'official_trucking_cost',
            min_value: 'official_trucking_product_min_value',
            weight_min: 'official_min_trucking',
            step: 'official_trucking_step',
            eta: 'official_trucking_eta',
            promo_role: 'promo_official_trucking_by_role'
        },
        trucking_unofficial: {
            currency: 'unofficial_trucking_currency',
            cost: 'unofficial_trucking_cost',
            min_value: 'unofficial_trucking_product_min_value',
            weight_min: 'unofficial_min_trucking',
            step: 'unofficial_trucking_step',
            eta: 'unofficial_trucking_eta',
            promo_role: 'promo_unofficial_trucking_by_role'
        }
    }), [])

    useEffect(() => {
        const fetchRateSettings = async () => {
            if (!sourceId || !destId) return
            // Collect all keys to fetch
            const keysToFetch = Object.values(RATE_KEYS).flatMap(config => Object.values(config)).join(',')

            const params = {
                'page[num]': 1,
                'page[limit]': 100,
                'filter[country_id][in]': destId,
                'filter[source_country_id][in]': `${sourceId},null`,
                'filter[key][in]': keysToFetch,
                'include': 'wholesale_prices',
                'fields[-global_settings]': 'updated_by'
            }

            const response = await getGlobalSettings(params)
            if (response.success && response.data) {
                setRateSettingsData(response.data)

                const newRates = { ...rates }
                const getValue = (key) => {
                    const item = response.data.find(d => d.attributes.key === key)
                    return item ? item.attributes.value : ''
                }

                const includedWholesalePrices = response.included?.wholesale_prices || {}

                Object.keys(RATE_KEYS).forEach(method => {
                    const methodKeys = RATE_KEYS[method]
                    const methodData = {}

                    const costItem = response.data.find(d => d.attributes.key === methodKeys.cost)

                    if (getValue(methodKeys.currency)) methodData.currency = getValue(methodKeys.currency)
                    if (getValue(methodKeys.cost)) methodData.cost = getValue(methodKeys.cost)
                    if (getValue(methodKeys.min_value)) methodData.min_value = getValue(methodKeys.min_value)
                    if (getValue(methodKeys.weight_min)) methodData.weight_min = getValue(methodKeys.weight_min)
                    if (getValue(methodKeys.step)) methodData.step = getValue(methodKeys.step)
                    if (getValue(methodKeys.eta)) methodData.eta = getValue(methodKeys.eta)

                    const wholesaleRelationships = costItem?.relationships?.wholesale_prices || []
                    if (wholesaleRelationships.length > 0) {
                        const prices = wholesaleRelationships.map(rel => {
                            const wp = includedWholesalePrices[rel.id]
                            if (wp) {
                                return {
                                    id: wp.id,
                                    min_quantity: Number(wp.attributes.min_quantity),
                                    max_quantity: wp.attributes.max_quantity || 0,
                                    price: String(wp.attributes.price)
                                }
                            }
                            return null
                        }).filter(wp => wp !== null)

                        // Sort by min_quantity
                        prices.sort((a, b) => a.min_quantity - b.min_quantity)

                        // Set max_quantity for each tier based on the next tier's min_quantity
                        methodData.wholesale_prices = prices.map((wp, index, arr) => {
                            let maxQ = wp.max_quantity
                            if (index < arr.length - 1) {
                                maxQ = arr[index + 1].min_quantity - 1
                            }
                            return {
                                ...wp,
                                min_quantity: String(wp.min_quantity),
                                max_quantity: maxQ
                            }
                        })

                        methodData.is_wholesale = methodData.wholesale_prices.length > 0
                    } else {
                        methodData.wholesale_prices = []
                        methodData.is_wholesale = false
                    }

                    if (Object.keys(methodData).length > 0) {
                        newRates[method] = { ...newRates[method], ...methodData }
                    }
                })

                setRates(newRates)
            }
        }

        fetchRateSettings()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceId, destId])

    const handleSaveRates = async (saveMethod = activeTab) => {
        if (!sourceId || !destId || !saveMethod || saveMethod === 'air_courier') return
        setLoading(true)

        const keys = RATE_KEYS[saveMethod]
        const currentRates = rates[saveMethod]

        if (!keys || !currentRates) {
            setLoading(false)
            return
        }

        const mapFieldToPayload = (fieldKey, value) => {
            const existing = rateSettingsData.find(d => d.attributes.key === fieldKey)
            return {
                id: existing ? existing.id : undefined,
                attributes: {
                    key: fieldKey,
                    country_id: parseInt(destId),
                    source_country_id: parseInt(sourceId),
                    value: String(value),
                    role_ids: existing?.attributes?.role_ids || null
                }
            }
        }

        const payloadData = []
        if (currentRates.currency !== undefined && currentRates.currency !== null) payloadData.push(mapFieldToPayload(keys.currency, currentRates.currency))
        if (currentRates.cost !== undefined && currentRates.cost !== null) payloadData.push(mapFieldToPayload(keys.cost, currentRates.cost))
        if (currentRates.min_value !== undefined && currentRates.min_value !== null) payloadData.push(mapFieldToPayload(keys.min_value, currentRates.min_value))
        if (currentRates.weight_min !== undefined && currentRates.weight_min !== null) payloadData.push(mapFieldToPayload(keys.weight_min, currentRates.weight_min))
        if (currentRates.step !== undefined && currentRates.step !== null) payloadData.push(mapFieldToPayload(keys.step, currentRates.step))
        if (currentRates.eta !== undefined && currentRates.eta !== null) payloadData.push(mapFieldToPayload(keys.eta, currentRates.eta))

        try {
            const bulkResponse = await updateGlobalSettingBulkUpdate({ data: payloadData })
            const updatedData = bulkResponse.data || []

            const costSetting = updatedData.find(d => d.attributes?.key === keys.cost) ||
                rateSettingsData.find(d => d.attributes?.key === keys.cost)

            if (costSetting) {
                if (currentRates.is_wholesale && currentRates.wholesale_prices?.length > 0) {
                    const wholesalePayload = {
                        type: "GlobalSetting"
                    }

                    // Final sort and calculation before sending
                    const sortedTiers = [...currentRates.wholesale_prices]
                        .sort((a, b) => Number(a.min_quantity || 0) - Number(b.min_quantity || 0))

                    wholesalePayload.data = sortedTiers.map((wp, index, arr) => {
                        let maxQ = wp.max_quantity
                        if (index < arr.length - 1) {
                            const nextMin = Number(arr[index + 1].min_quantity || 0)
                            if (nextMin > 0) {
                                maxQ = nextMin - 1
                            }
                        } else {
                            maxQ = 0 // Last tier max is 0 (unlimited)
                        }

                        return {
                            min_quantity: String(wp.min_quantity),
                            max_quantity: Number(maxQ),
                            price: String(wp.price)
                        }
                    })

                    await updateWholesalePrice(costSetting.id, wholesalePayload)
                } else {
                    await deleteWholesalePrice(costSetting.id)
                }
            }

            notifications.show({ title: 'Success', message: `${saveMethod.replace('_', ' ').toUpperCase()} rates updated successfully`, color: 'green' })
        } catch (error) {
            console.error(error)
            notifications.show({ title: 'Error', message: 'Failed to update rates', color: 'red' })
        } finally {
            setLoading(false)
        }
    }

    const handleToggleMethod = (value) => {
        setAvailableMethods(prev => {
            if (prev.includes(value)) {
                const original = originalMethods.find(m => m.attributes.value === value)
                if (original) {
                    setMethodsToDelete(prevIds => [...new Set([...prevIds, original.id])])
                }
                return prev.filter(p => p !== value)
            } else {
                const original = originalMethods.find(m => m.attributes.value === value)
                if (original) {
                    setMethodsToDelete(prevIds => prevIds.filter(id => String(id) !== String(original.id)))
                }
                return [...prev, value]
            }
        })
    }

    const handleRateChange = (method, field, value) => {
        setRates(prev => ({
            ...prev,
            [method]: {
                ...prev[method],
                [field]: value
            }
        }))
    }

    const handleSave = async () => {
        if (!sourceId || !destId) return
        setLoading(true)

        const payloadData = []

        availableMethods.forEach(method => {
            payloadData.push({
                attributes: {
                    key: "available_shipment_methods",
                    country_id: parseInt(destId),
                    source_country_id: parseInt(sourceId),
                    value: method
                }
            })
        })

        if (officialVat) {
            payloadData.push({
                attributes: {
                    key: "official_vat",
                    country_id: parseInt(destId),
                    source_country_id: parseInt(sourceId),
                    value: officialVat
                }
            })
        }

        if (routes) {
            payloadData.push({
                attributes: {
                    key: "official_routes",
                    country_id: parseInt(destId),
                    source_country_id: parseInt(sourceId),
                    value: routes
                }
            })
        }

        if (defaultMethod) {
            payloadData.push({
                attributes: {
                    key: "default_shipment_method",
                    country_id: parseInt(destId),
                    source_country_id: parseInt(sourceId),
                    value: defaultMethod
                }
            })
        }

        try {
            if (methodsToDelete.length > 0) {
                await deleteGlobalSettingBulkDelete({ data: methodsToDelete.map(id => ({ id })) })
                setMethodsToDelete([])
                setOriginalMethods(prev => prev.filter(m => !methodsToDelete.includes(m.id)))
            }
            await updateGlobalSetting({ data: payloadData })
            notifications.show({ title: 'Success', message: 'Settings saved successfully', color: 'green' })
        } catch (error) {
            console.error(error)
            notifications.show({ title: 'Error', message: 'Failed to save settings', color: 'red' })
        } finally {
            setLoading(false)
        }
    }

    const activeTabList = useMemo(() => {
        return ALL_METHODS.filter(m => availableMethods.includes(m.value))
    }, [availableMethods])

    useEffect(() => {
        if (activeTabList.length > 0 && !activeTab) {
            setActiveTab(activeTabList[0]?.value)
        }
    }, [activeTabList, activeTab])

    const fetchAirCourierRates = useCallback(async () => {
        if (activeTab !== 'air_courier' || !sourceId || !destId) return

        setAirCourierLoading(true)
        try {
            const params = {
                'page[num]': 1,
                'page[limit]': 1000000,
                'filter[origin_country_id][is]': sourceId,
                'filter[destination_country_id][is]': destId,
                'shipping_types': 'International',
                'shipping_method': 'courier',
                'diverse': 'International',
                'sort': 'rate_per_weight',
                'include': 'country,courier,destinationCountry'
            }

            const response = await getLogisticRates(params)
            if (response.success && response.data) {
                setAirCourierRates(response.data)
                if (response.included) {
                    setAirCourierIncluded(response.included)
                }
            }
        } catch (error) {
            console.error('Failed to fetch air courier rates', error)
            notifications.show({ title: 'Error', message: 'Failed to load Air Courier rates', color: 'red' })
        } finally {
            setAirCourierLoading(false)
        }
    }, [activeTab, sourceId, destId])

    useEffect(() => {
        fetchAirCourierRates()
    }, [fetchAirCourierRates])

    const getCourierName = (courierId) => {
        if (airCourierIncluded?.courier && airCourierIncluded.courier[courierId]) {
            return airCourierIncluded.courier[courierId].attributes.name
        }
        return `Courier ${courierId}`
    }

    const uniqueAirCourierRates = useMemo(() => {
        const uniqueMap = {}
        airCourierRates.forEach(rate => {
            const key = `${rate.attributes.origin_id}-${rate.attributes.destination_id}-${rate.attributes.courier_id}`
            if (!uniqueMap[key]) {
                uniqueMap[key] = rate
            }
        })
        return Object.values(uniqueMap)
    }, [airCourierRates])

    const handleEditRate = async (rate) => {
        setEditModalLoading(true)
        setEditModalOpen(true)

        try {
            const params = {
                'page[num]': 1,
                'page[limit]': 1000000,
                'filter[origin_country_id][is]': sourceId,
                'filter[destination_country_id][is]': destId,
                'sort': 'rate_per_weight',
                'diverse': 'International',
                'include': 'country,courier,destinationCountry',
                'filter[origin_id][is]': rate.attributes.origin_id,
                'filter[destination_id][is]': rate.attributes.destination_id,
                'filter[courier_id][is]': rate.attributes.courier_id
            }

            const response = await getLogisticRates(params)
            if (response.success && response.data && response.data.length > 0) {
                const firstRate = response.data[0]
                const advanceRates = response.data.map(r => ({
                    id: r.id,
                    rate_per_weight: r.attributes.rate_per_weight,
                    rate: r.attributes.rate
                }))

                setEditingRate(firstRate)
                setEditFormData({
                    shipping_types: firstRate.attributes.shipping_types || 'International',
                    shipping_method: firstRate.attributes.shipping_method || 'courier',
                    origin_country_id: firstRate.attributes.origin_country_id,
                    origin_type: firstRate.attributes.origin_type || 'cities',
                    destination_country_id: firstRate.attributes.destination_country_id,
                    destination_type: firstRate.attributes.destination_type || 'Country',
                    origin_id: firstRate.attributes.origin_id,
                    origin_name: firstRate.attributes.origin?.name || '',
                    destination_id: firstRate.attributes.destination_id,
                    destination_name: firstRate.attributes.destination?.name || '',
                    calculation: firstRate.attributes.calculation || 'flat',
                    currency: firstRate.attributes.currency || 'IDR',
                    weight_unit: firstRate.attributes.weight_unit || 'kg',
                    courier_id: firstRate.attributes.courier_id,
                    courier_name: getCourierName(firstRate.attributes.courier_id),
                    minimum: firstRate.attributes.minimum || 1,
                    maximum: firstRate.attributes.maximum || 1000,
                    insurance: firstRate.attributes.insurance > 0,
                    insurance_percentage: parseFloat(firstRate.attributes.insurance_percentage) || 0,
                    remarks: firstRate.attributes.remarks || '',
                    name: firstRate.attributes.name || '',
                    advance_rates: advanceRates
                })
            }
        } catch (error) {
            console.error('Failed to fetch rate details', error)
            notifications.show({ title: 'Error', message: 'Failed to load rate details', color: 'red' })
            setEditModalOpen(false)
        } finally {
            setEditModalLoading(false)
        }
    }

    // Handle detail view - open detail modal
    const handleDetailRate = async (rate) => {
        setDetailModalLoading(true)
        setDetailModalOpen(true)

        try {
            const params = {
                'page[num]': 1,
                'page[limit]': 1000000,
                'filter[origin_country_id][is]': sourceId,
                'filter[destination_country_id][is]': destId,
                'sort': 'rate_per_weight',
                'diverse': 'International',
                'include': 'country,courier,destinationCountry',
                'filter[origin_id][is]': rate.attributes.origin_id,
                'filter[destination_id][is]': rate.attributes.destination_id,
                'filter[courier_id][is]': rate.attributes.courier_id
            }

            const response = await getLogisticRates(params)
            if (response.success && response.data && response.data.length > 0) {
                const firstRate = response.data[0]
                setDetailRateInfo({
                    name: firstRate.attributes.name || '-',
                    courier: getCourierName(firstRate.attributes.courier_id),
                    origin: firstRate.attributes.origin?.name || '-',
                    destination: firstRate.attributes.destination?.name || '-',
                    calculation: firstRate.attributes.calculation || 'flat',
                    minimum: firstRate.attributes.minimum || 0,
                    maximum: firstRate.attributes.maximum || 0,
                    remarks: firstRate.attributes.remarks || '-',
                    currency: firstRate.attributes.currency || 'IDR',
                    weight_unit: firstRate.attributes.weight_unit || 'kg'
                })
                setDetailRateList(response.data.map(r => ({
                    id: r.id,
                    rate_per_weight: r.attributes.rate_per_weight,
                    rate: r.attributes.rate
                })))
            }
        } catch (error) {
            console.error('Failed to fetch rate details', error)
            notifications.show({ title: 'Error', message: 'Failed to load rate details', color: 'red' })
            setDetailModalOpen(false)
        } finally {
            setDetailModalLoading(false)
        }
    }

    const handleDeleteRate = async (rate) => {
        const ids = airCourierRates
            .filter(r =>
                r.attributes.origin_id === rate.attributes.origin_id &&
                r.attributes.destination_id === rate.attributes.destination_id &&
                r.attributes.courier_id === rate.attributes.courier_id
            )
            .map(r => r.id)

        if (ids.length === 0) return

        modals.openConfirmModal({
            title: 'Delete Rate Group',
            children: (
                <Text size="sm">
                    Are you sure you want to delete these rates? This action cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setAirCourierLoading(true)
                try {
                    await deleteLogisticRates({ ids })
                    notifications.show({ title: 'Success', message: 'Rates deleted successfully', color: 'green' })
                    fetchAirCourierRates()
                } catch (error) {
                    console.error(error)
                    notifications.show({ title: 'Error', message: 'Failed to delete rates', color: 'red' })
                } finally {
                    setAirCourierLoading(false)
                }
            }
        })
    }
    const handleAddNewRate = () => {
        setEditingRate(null)
        setEditFormData({
            shipping_types: 'International',
            shipping_method: 'courier',
            origin_country_id: parseInt(sourceId),
            origin_type: 'cities',
            destination_country_id: parseInt(destId),
            destination_type: 'Country',
            origin_id: null,
            origin_name: '',
            destination_id: parseInt(destId),
            destination_name: destCountry?.attributes?.name || '',
            calculation: 'flat',
            currency: 'IDR',
            weight_unit: 'kg',
            courier_id: null,
            courier_name: '',
            minimum: 1,
            maximum: 1000,
            insurance: false,
            insurance_percentage: 0,
            remarks: '',
            name: '',
            advance_rates: [{ rate_per_weight: '', rate: '' }]
        })
        setEditModalOpen(true)
    }

    // Handle save rate
    const handleSaveRate = async () => {
        if (!editFormData.courier_id) {
            notifications.show({ title: 'Error', message: 'Please select a courier', color: 'red' })
            return
        }
        if (editFormData.advance_rates.some(r => !r.rate || !r.rate_per_weight)) {
            notifications.show({ title: 'Error', message: 'Please fill all rate details', color: 'red' })
            return
        }

        setEditModalLoading(true)
        try {
            const promises = editFormData.advance_rates.map(rateItem => {
                const payload = {
                    shipping_types: editFormData.shipping_types,
                    shipping_method: editFormData.shipping_method,
                    origin_country_id: Number(sourceCountry?.id),
                    origin_id: String(editFormData.origin_id),
                    origin_type: editFormData.origin_type,
                    destination_country_id: Number(destCountry?.id),
                    destination_id: String(editFormData.destination_id),
                    destination_type: editFormData.destination_type,
                    calculation: editFormData.calculation,
                    currency: editFormData.currency,
                    weight_unit: editFormData.weight_unit,
                    courier_id: String(editFormData.courier_id),
                    maximum: String(editFormData.maximum),
                    minimum: String(editFormData.minimum),
                    name: editFormData.name,
                    remarks: editFormData.remarks,
                    insurance: editFormData.insurance ? 1 : 0,
                    relationships: [],
                    rate: String(rateItem.rate),
                    rate_per_weight: String(rateItem.rate_per_weight)
                }
                return createLogisticRate(payload)
            })

            await Promise.all(promises)

            notifications.show({ title: 'Success', message: 'Rates saved successfully', color: 'green' })
            setEditModalOpen(false)
            fetchAirCourierRates()
        } catch (error) {
            console.error('Failed to save rates', error)
            notifications.show({ title: 'Error', message: error.message || 'Failed to save rates', color: 'red' })
        } finally {
            setEditModalLoading(false)
        }
    }

    // Modals
    const renderDetailModal = () => (
        <ModalDetailRate
            opened={detailModalOpen}
            onClose={() => setDetailModalOpen(false)}
            loading={detailModalLoading}
            rateInfo={detailRateInfo}
            rateList={detailRateList}
        />
    )

    const renderEditModal = () => (
        <ModalEditRate
            opened={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            loading={editModalLoading}
            isEditing={!!editingRate}
            formData={editFormData}
            setFormData={setEditFormData}
            sourceCountry={sourceCountry}
            destCountry={destCountry}
            onSave={handleSaveRate}
        />
    )

    return (
        <AuthLayout>
            <Box my={20}>
                {/* Header */}
                <Box mb={20}>
                    <Text className={classes.titleLayout}>International Shipping Rate List</Text>
                    <Text c="dimmed" size="sm">Settings logistic cost on every countries</Text>
                    <Group wrap="nowrap" mt="md" align="center">
                        <Text fz={24} fw={700}>
                            {sourceCountry?.attributes?.name || 'Loading...'}
                            <span style={{ margin: '0 10px', color: '#999' }}>&gt;</span>
                            {destCountry?.attributes?.name || 'Loading...'}
                        </Text>
                    </Group>
                </Box>

                <Card withBorder p="lg" pos="relative" radius="md">
                    <LoadingOverlay visible={pageLoading} />

                    <ShipmentMethodSection
                        methodGroups={METHOD_GROUPS}
                        availableMethods={availableMethods}
                        handleToggleMethod={handleToggleMethod}
                    />

                    <Box mt="xl">
                        <GeneralConfiguration
                            officialVat={officialVat}
                            setOfficialVat={setOfficialVat}
                            routes={routes}
                            setRoutes={setRoutes}
                            defaultMethod={defaultMethod}
                            setDefaultMethod={setDefaultMethod}
                            sourceCountry={sourceCountry}
                            activeTabList={activeTabList}
                            handleSave={handleSave}
                            loading={loading}
                        />
                    </Box>
                </Card>

                {activeTabList.length > 0 && (
                    <Box mt={30}>
                        <Tabs
                            value={activeTab}
                            onChange={setActiveTab}
                            variant="outline"
                            radius="md"
                        >
                            <Tabs.List>
                                {activeTabList.map(method => (
                                    <Tabs.Tab
                                        key={method.value}
                                        value={method.value}
                                        fw={600}
                                        c={rates[method.value] || (method.value === 'air_courier' && airCourierRates.length > 0) ? 'blue' : undefined}
                                    >
                                        {method.label}
                                    </Tabs.Tab>
                                ))}
                            </Tabs.List>

                            {activeTabList.map(method => (
                                <Tabs.Panel key={method.value} value={method.value}>
                                    {method.value === 'air_courier' ? (
                                        <AirCourierPanel
                                            loading={airCourierLoading}
                                            uniqueRates={uniqueAirCourierRates}
                                            handleAddNewRate={handleAddNewRate}
                                            getCourierName={getCourierName}
                                            handleDetailRate={handleDetailRate}
                                            handleEditRate={handleEditRate}
                                            handleDeleteRate={handleDeleteRate}
                                            sourceCountry={sourceCountry}
                                            destCountry={destCountry}
                                            fetchAirCourierRates={fetchAirCourierRates}
                                        />
                                    ) : (
                                        <FreightRatePanel
                                            method={method}
                                            rates={rates}
                                            onRateChange={handleRateChange}
                                            onSave={handleSaveRates}
                                            loading={loading}
                                        />
                                    )}
                                </Tabs.Panel>
                            ))}
                        </Tabs>
                    </Box>
                )}

            </Box>
            {renderEditModal()}
            {renderDetailModal()}
        </AuthLayout>
    )
}

export default SetRates
